import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Plus, Pencil, Trash2, ExternalLink, Copy, LogOut, Loader2, Lock, ShieldCheck,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Logo } from "@/components/Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "aws_admin_token";
const PUBLIC_DOMAIN = "https://anchorwebsystems.com";

const slugify = (v) =>
  (v || "").trim().toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");

const emptyForm = { business_name: "", slug: "", external_url: "", internal_notes: "", active: true };

export default function AdminDemos() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const authHeader = useMemo(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);

  // robots noindex
  useEffect(() => {
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex, nofollow, noarchive";
    document.head.appendChild(meta);
    return () => document.head.removeChild(meta);
  }, []);

  if (!token) {
    return <Login onSuccess={(t) => { localStorage.setItem(TOKEN_KEY, t); setToken(t); }} />;
  }
  return (
    <Manager
      authHeader={authHeader}
      onLogout={() => { localStorage.removeItem(TOKEN_KEY); setToken(""); }}
    />
  );
}

/* -------------------------- Login -------------------------- */
const Login = ({ onSuccess }) => {
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);
    try {
      const res = await axios.post(`${API}/admin/login`, { password });
      toast.success("Welcome back.");
      onSuccess(res.data.token);
    } catch (err) {
      const msg = err?.response?.status === 401 ? "Incorrect password." : "Login unavailable.";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="grid min-h-screen w-full place-items-center bg-gradient-navy px-6">
      <div className="w-full max-w-sm rounded-3xl border border-white/10 bg-navy p-8 shadow-elegant">
        <div className="mb-6 flex flex-col items-center text-center">
          <span className="grid h-12 w-12 place-items-center rounded-2xl bg-gradient-blue shadow-blue">
            <Lock className="h-6 w-6 text-primary-foreground" />
          </span>
          <h1 className="mt-4 font-display text-xl font-bold text-navy-foreground">Demo Manager</h1>
          <p className="mt-1 text-sm text-navy-muted">Admin access required</p>
        </div>
        <form onSubmit={submit} className="space-y-4" noValidate>
          <div className="space-y-1.5">
            <Label htmlFor="password" className="text-navy-foreground">Password</Label>
            <Input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter admin password"
              autoFocus
              className="h-11 rounded-xl border-white/15 bg-white/5 text-navy-foreground placeholder:text-navy-muted"
            />
          </div>
          <Button type="submit" variant="cta" size="lg" className="w-full" disabled={loading}>
            {loading ? <><Loader2 className="h-4 w-4 animate-spin" /> Signing in…</> : <><ShieldCheck className="h-4 w-4" /> Sign In</>}
          </Button>
        </form>
      </div>
    </div>
  );
};

/* -------------------------- Manager -------------------------- */
const Manager = ({ authHeader, onLogout }) => {
  const [demos, setDemos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null); // demo being edited or null (create)
  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/demos`, authHeader);
      setDemos(res.data);
    } catch (err) {
      if (err?.response?.status === 401) {
        toast.error("Session expired. Please sign in again.");
        onLogout();
      } else {
        toast.error("Could not load demos.");
      }
    } finally {
      setLoading(false);
    }
  }, [authHeader, onLogout]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEditing(null); setDialogOpen(true); };
  const openEdit = (demo) => { setEditing(demo); setDialogOpen(true); };

  const toggleActive = async (demo) => {
    try {
      await axios.put(`${API}/admin/demos/${demo.id}`, { active: !demo.active }, authHeader);
      toast.success(demo.active ? "Demo deactivated." : "Demo activated.");
      load();
    } catch {
      toast.error("Could not update status.");
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await axios.delete(`${API}/admin/demos/${deleteTarget.id}`, authHeader);
      toast.success("Demo deleted.");
      setDeleteTarget(null);
      load();
    } catch {
      toast.error("Could not delete demo.");
    }
  };

  const copyLink = async (slug) => {
    const url = `${PUBLIC_DOMAIN}/demo/${slug}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Public link copied.");
    } catch {
      toast.error("Copy failed. Link: " + url);
    }
  };

  return (
    <div className="min-h-screen w-full bg-secondary/40">
      {/* Top bar */}
      <header className="sticky top-0 z-20 border-b border-border bg-background/90 backdrop-blur">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-5 py-3 lg:px-8">
          <div className="flex items-center gap-3">
            <Logo variant="light" />
            <Badge variant="secondary" className="hidden sm:inline-flex">Demo Manager</Badge>
          </div>
          <Button variant="outline" size="sm" onClick={onLogout}>
            <LogOut className="h-4 w-4" /> Sign out
          </Button>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-5 py-8 lg:px-8">
        <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="font-display text-2xl font-bold text-navy">Client Demos</h1>
            <p className="mt-1 text-sm text-muted-foreground">
              Manage private, iframe-based client website demos.
            </p>
          </div>
          <Button variant="cta" onClick={openCreate}>
            <Plus className="h-4 w-4" /> Add Demo
          </Button>
        </div>

        <div className="overflow-hidden rounded-2xl border border-border bg-card shadow-sm">
          {loading ? (
            <div className="flex items-center justify-center gap-2 py-16 text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin" /> Loading…
            </div>
          ) : demos.length === 0 ? (
            <div className="py-16 text-center text-sm text-muted-foreground">
              No demos yet. Click “Add Demo” to create your first one.
            </div>
          ) : (
            <div className="divide-y divide-border">
              {demos.map((d) => (
                <div key={d.id} className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="truncate font-display text-base font-semibold text-navy">
                        {d.business_name}
                      </span>
                      <Badge
                        variant={d.active ? "default" : "secondary"}
                        className={d.active ? "bg-success text-success-foreground hover:bg-success" : ""}
                      >
                        {d.active ? "Active" : "Inactive"}
                      </Badge>
                    </div>
                    <p className="mt-0.5 truncate text-xs text-muted-foreground">
                      /demo/{d.slug}
                      <span className="mx-2 text-border">•</span>
                      Updated {new Date(d.updated_at).toLocaleDateString()}
                    </p>
                  </div>

                  <div className="flex flex-wrap items-center gap-2">
                    <div className="flex items-center gap-1.5 rounded-lg border border-border px-2.5 py-1">
                      <Switch
                        id={`active-${d.id}`}
                        checked={d.active}
                        onCheckedChange={() => toggleActive(d)}
                        aria-label={d.active ? "Deactivate demo" : "Activate demo"}
                      />
                      <Label htmlFor={`active-${d.id}`} className="cursor-pointer text-xs text-muted-foreground">
                        {d.active ? "On" : "Off"}
                      </Label>
                    </div>
                    <Button variant="outline" size="sm" onClick={() => copyLink(d.slug)}>
                      <Copy className="h-4 w-4" /> Copy link
                    </Button>
                    <Button asChild variant="outline" size="sm">
                      <a href={`/demo/${d.slug}`} target="_blank" rel="noopener noreferrer">
                        <ExternalLink className="h-4 w-4" /> Preview
                      </a>
                    </Button>
                    <Button variant="outline" size="icon" onClick={() => openEdit(d)} aria-label={`Edit ${d.business_name}`}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="icon"
                      className="text-destructive hover:bg-destructive/10"
                      onClick={() => setDeleteTarget(d)}
                      aria-label={`Delete ${d.business_name}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>

      <DemoDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        editing={editing}
        authHeader={authHeader}
        onSaved={load}
      />

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this demo?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete the demo for{" "}
              <span className="font-semibold text-navy">{deleteTarget?.business_name}</span>. This
              action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

/* -------------------------- Add / Edit dialog -------------------------- */
const DemoDialog = ({ open, onOpenChange, editing, authHeader, onSaved }) => {
  const [form, setForm] = useState(emptyForm);
  const [slugEdited, setSlugEdited] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      if (editing) {
        setForm({
          business_name: editing.business_name,
          slug: editing.slug,
          external_url: editing.external_url,
          internal_notes: editing.internal_notes || "",
          active: editing.active,
        });
        setSlugEdited(true);
      } else {
        setForm(emptyForm);
        setSlugEdited(false);
      }
    }
  }, [open, editing]);

  const onName = (v) => {
    setForm((f) => ({ ...f, business_name: v, slug: slugEdited ? f.slug : slugify(v) }));
  };
  const onSlug = (v) => {
    setSlugEdited(true);
    setForm((f) => ({ ...f, slug: slugify(v) }));
  };

  const save = async (e) => {
    e.preventDefault();
    if (saving) return;
    if (!form.business_name.trim()) return toast.error("Business name is required.");
    if (!form.slug.trim()) return toast.error("Slug is required.");
    if (!/^https:\/\//i.test(form.external_url.trim()))
      return toast.error("External URL must start with https://");

    setSaving(true);
    try {
      if (editing) {
        await axios.put(`${API}/admin/demos/${editing.id}`, form, authHeader);
        toast.success("Demo updated.");
      } else {
        await axios.post(`${API}/admin/demos`, form, authHeader);
        toast.success("Demo created.");
      }
      onOpenChange(false);
      onSaved();
    } catch (err) {
      const s = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (s === 409) toast.error(detail || "That slug is already in use.");
      else if (s === 422) toast.error(detail || "Please check the fields and try again.");
      else if (s === 401) toast.error("Session expired. Please sign in again.");
      else toast.error("Could not save demo.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{editing ? "Edit demo" : "Add demo"}</DialogTitle>
          <DialogDescription>
            {editing ? "Update this client demo." : "Create a new private client website demo."}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={save} className="space-y-4" noValidate>
          <div className="space-y-1.5">
            <Label htmlFor="d-name">Client / business name</Label>
            <Input id="d-name" value={form.business_name} onChange={(e) => onName(e.target.value)} placeholder="La Chupaleta Ice Cream & More" className="h-11 rounded-xl" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="d-slug">Slug</Label>
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground">/demo/</span>
              <Input id="d-slug" value={form.slug} onChange={(e) => onSlug(e.target.value)} placeholder="lachupaicecream" className="h-11 rounded-xl" />
            </div>
            <p className="text-xs text-muted-foreground">Auto-generated from the name. Lowercase letters, numbers and hyphens only.</p>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="d-url">External demo URL (HTTPS)</Label>
            <Input id="d-url" type="url" value={form.external_url} onChange={(e) => setForm((f) => ({ ...f, external_url: e.target.value }))} placeholder="https://example.com" className="h-11 rounded-xl" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="d-notes">Internal notes (private)</Label>
            <Textarea id="d-notes" value={form.internal_notes} onChange={(e) => setForm((f) => ({ ...f, internal_notes: e.target.value }))} placeholder="Only visible here in the admin." className="min-h-[80px] rounded-xl" />
          </div>
          <div className="flex items-center gap-3 rounded-xl border border-border p-3">
            <Switch id="d-active" checked={form.active} onCheckedChange={(v) => setForm((f) => ({ ...f, active: v }))} aria-label="Active status" />
            <Label htmlFor="d-active" className="cursor-pointer">Active (visible on the public demo link)</Label>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" variant="cta" disabled={saving}>
              {saving ? <><Loader2 className="h-4 w-4 animate-spin" /> Saving…</> : (editing ? "Save changes" : "Create demo")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
